import { useState } from "react";
import { PageHero } from "@/pages/About";
import { Field, TextInput, TextArea, SelectField, ConsentCheckbox, Honeypot } from "@/components/FormFields";
import { useFormSubmit } from "@/lib/useFormSubmit";
import { SuccessCard } from "@/pages/WarrenParkConnect";
import { Mail, MapPin, Globe } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const RESPONSE_METHODS = ["Email", "Phone", "WhatsApp", "No preference"];

export default function Contact() {
    return (
        <div>
            <PageHero title="Contact Us" subtitle="We would love to hear from you" />
            <section className="gsf-section" data-testid="contact-section">
                <div className="gsf-container grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="gsf-eyebrow">Reach Us</div>
                        <h2 className="mt-3 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Talk to Global Seed Foundation</h2>
                        <ul className="mt-6 space-y-4 text-[color:var(--gsf-body)]">
                            <li className="flex items-start gap-3">
                                <Mail size={18} className="mt-1 text-[color:var(--gsf-green-700)]" />
                                <a href="mailto:impact@globalseedzim.org" className="hover:underline" data-testid="contact-email">impact@globalseedzim.org</a>
                            </li>
                            <li className="flex items-start gap-3">
                                <WhatsAppIcon size={18} className="mt-1 text-[color:var(--gsf-green-700)]" />
                                <a
                                    href="https://wa.me/263782658473"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Chat with Global Seed Foundation on WhatsApp"
                                    className="hover:underline"
                                    data-testid="contact-whatsapp"
                                >
                                    WhatsApp: +263 78 265 8473
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Globe size={18} className="mt-1 text-[color:var(--gsf-green-700)]" />
                                <span>www.globalseedzim.org</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin size={18} className="mt-1 text-[color:var(--gsf-green-700)]" />
                                <span>Warren Park Constituency and surrounding communities, Harare, Zimbabwe</span>
                            </li>
                        </ul>
                    </div>
                    <div className="md:col-span-7">
                        <ContactForm />
                    </div>
                </div>
            </section>
        </div>
    );
}

function ContactForm() {
    const [s, setS] = useState({ full_name: "", org: "", email: "", phone: "", subject: "", message: "", preferred_response: "Email", website: "" });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("contact");
    if (done) return <SuccessCard testid="contact-success">Thank you — your message has been received. We usually respond within 2 working days.</SuccessCard>;
    return (
        <form onSubmit={async (e) => { e.preventDefault(); await submit(s, consent); }} className="gsf-card p-8 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="c-name" label="Full name" required><TextInput id="c-name" testid="c-name" required value={s.full_name} onChange={(v) => setS({ ...s, full_name: v })} /></Field>
                <Field id="c-org" label="Organisation"><TextInput id="c-org" testid="c-org" value={s.org} onChange={(v) => setS({ ...s, org: v })} /></Field>
                <Field id="c-email" label="Email" required><TextInput id="c-email" testid="c-email" type="email" required value={s.email} onChange={(v) => setS({ ...s, email: v })} /></Field>
                <Field id="c-phone" label="Phone / WhatsApp"><TextInput id="c-phone" testid="c-phone" value={s.phone} onChange={(v) => setS({ ...s, phone: v })} /></Field>
            </div>
            <Field id="c-subject" label="Subject" required><TextInput id="c-subject" testid="c-subject" required value={s.subject} onChange={(v) => setS({ ...s, subject: v })} /></Field>
            <Field id="c-msg" label="Message" required><TextArea id="c-msg" testid="c-msg" rows={5} required value={s.message} onChange={(v) => setS({ ...s, message: v })} /></Field>
            <Field id="c-resp" label="Preferred response method">
                <SelectField id="c-resp" testid="c-resp" value={s.preferred_response} onChange={(v) => setS({ ...s, preferred_response: v })} placeholder="Preferred response" options={RESPONSE_METHODS} />
            </Field>
            <Honeypot value={s.website} onChange={(v) => setS({ ...s, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="c-consent" />
            {error && <p className="text-sm text-red-600" data-testid="c-error">{error}</p>}
            <button className="gsf-btn-primary disabled:opacity-60" disabled={submitting} data-testid="c-submit">{submitting ? "Sending…" : "Send Message"}</button>
        </form>
    );
}
